import React, { useRef, useEffect } from "react";
import * as THREE from 'three';
import { useThree } from "@react-three/fiber";

import { sceneUnitsFromMm } from "../utils/scaler";

import LayerMappingsEnum from '../enums/LayerMappingsEnum';

export interface Stage3dProps {
    center: THREE.Vector3;
    stageBounds: THREE.Box3;
    isDebug?: boolean;
}

export const USE_SHADOWS = false;
const DIRECTIONAL_LIGHT_INTENSITY = 1.0;
const DIRECTIONAL_LIGHT_CAST_SHADOW = USE_SHADOWS;

const Stage3d = (props: Stage3dProps) => {

    if (USE_SHADOWS) {
        const {gl} = useThree();
        gl.shadowMap.enabled = true;
        gl.shadowMap.type = THREE.VSMShadowMap; 
    }

    // Directional light #1
    const dl1Ref = useRef<THREE.DirectionalLight>(null);
    const dl1HelperRef = useRef<THREE.DirectionalLightHelper>(null);
    
    // Directional light #2
    const dl2Ref = useRef<THREE.DirectionalLight>(null);
    const dl2HelperRef = useRef<THREE.DirectionalLightHelper>(null);

    // Directional light #3
    const dl3Ref = useRef<THREE.DirectionalLight>(null);
    const dl3HelperRef = useRef<THREE.DirectionalLightHelper>(null);
    
    const sharedTargetRef = useRef<THREE.Mesh>(null);

    const stageLayers = new THREE.Layers();
    stageLayers.set(LayerMappingsEnum.Stage);

    const debugLayers = new THREE.Layers();
    debugLayers.set(LayerMappingsEnum.DebugEntities);
    
    const stageCenter = props.stageBounds.getCenter(new THREE.Vector3);
    const offset = props.stageBounds.max.clone().sub(stageCenter);
    
    // Calculate positions for directional lights
    const lightPositions : THREE.Vector3[] = [];
    for (let i=0; i < 3; i++) {
        const rotatedPos = offset.clone().applyAxisAngle(new THREE.Vector3(0,0,1), i * (Math.PI*2/3)); // 120 degree rotation
        lightPositions.push(rotatedPos.add(stageCenter));
    }

    // Finetune heights
    lightPositions[0].z -= sceneUnitsFromMm(3000);
    lightPositions[1].z += sceneUnitsFromMm(1000);
    lightPositions[2].z += sceneUnitsFromMm(4000);

    const applyShadowSettings = (dirLight: THREE.DirectionalLight) => {
        dirLight.shadow.mapSize.set(2048, 2048);
        dirLight.shadow.radius = 6;
        dirLight.shadow.blurSamples = 12;
        dirLight.shadow.bias = -0.0008;
        dirLight.shadow.normalBias = 0.04;
    }
    useEffect(() => {

        if (dl1Ref.current && sharedTargetRef.current) {
            dl1Ref.current.target = sharedTargetRef.current;
            applyShadowSettings(dl1Ref.current);
            dl1HelperRef.current?.update();       
            
        }

    }, [dl1Ref.current, sharedTargetRef.current]);

    useEffect(() => {

        if (dl2Ref.current && sharedTargetRef.current) {
            dl2Ref.current.target = sharedTargetRef.current;
            applyShadowSettings(dl2Ref.current);
            dl2HelperRef.current?.update();                                                            
        }

    }, [dl2Ref.current, sharedTargetRef.current]);

    useEffect(() => {

        if (dl3Ref.current && sharedTargetRef.current) {
            dl3Ref.current.target = sharedTargetRef.current;
            applyShadowSettings(dl3Ref.current);
            dl3HelperRef.current?.update();                                                            
        }

    }, [dl3Ref.current, sharedTargetRef.current]);

    return (
        <>
            {/* <ambientLight layers={Layers2d} intensity={1.0} />  */}       

        {/* Target for directional light */}
        <mesh name="Light-target" ref={sharedTargetRef} position={props.center} layers={debugLayers}>
            <boxGeometry    args={[     sceneUnitsFromMm(1000), 
                                        sceneUnitsFromMm(1000), 
                                        sceneUnitsFromMm(1000)]} />
            <meshStandardMaterial color={"hotpink"} opacity={0.9} transparent={true}/>
        </mesh>

        {/* <FbxLoaderTest /> */}

<group name="3D Stage Lights">
    <directionalLight name="Directional Light #1" ref={dl1Ref} 
                        position={lightPositions[0]}
                        layers={stageLayers} 
                        intensity={DIRECTIONAL_LIGHT_INTENSITY * 0.6} 
                            castShadow={DIRECTIONAL_LIGHT_CAST_SHADOW}  
                            
                            />

    <directionalLight name="Directional Light #2" ref={dl2Ref} 
                            position={lightPositions[1]}
                            layers={stageLayers} 
                            intensity={DIRECTIONAL_LIGHT_INTENSITY * 2.5} 
                            castShadow={DIRECTIONAL_LIGHT_CAST_SHADOW}  
                            />

    <directionalLight name="Directional Light #3" ref={dl3Ref} 
                            position={lightPositions[2]}
                            layers={stageLayers} 
                            intensity={DIRECTIONAL_LIGHT_INTENSITY * 1.5} 
                            castShadow={DIRECTIONAL_LIGHT_CAST_SHADOW}  
                            />


        {/* Helper to visualize position and direction of the directional light #1 */}
        { dl1Ref.current &&
            <directionalLightHelper     
            name="Directional Light #1 Helper"
            ref={dl1HelperRef} 
            args={[dl1Ref.current, 2, 0xff0000]}
            //visible={props.isDebug}/> /* Helpers do not obey layers, so doing this the hard way */
            visible={false}/>
        }

        {/* Helper to visualize position and direction of the directional light #1 */}
        { dl2Ref.current &&
            <directionalLightHelper     name="Directional Light #2 Helper"
            ref={dl2HelperRef} 
            args={[dl2Ref.current, 1, "green"]}
            //visible={props.isDebug}/> /* Helpers do not obey layers, so doing this the hard way */
            visible={false}/>
        }

        { dl3Ref.current &&
            <directionalLightHelper     name="Directional Light #3 Helper"
            ref={dl3HelperRef} 
            args={[dl3Ref.current, 1, "blue"]}
            //visible={props.isDebug}/> /* Helpers do not obey layers, so doing this the hard way */
            visible={false}/>
        }

            </group> 
        </>
    );
};

export default Stage3d;